import React from "react"
import { useHourReportMutations } from "./use-hour-report-mutations"
import { type HourReportFormState, createHourReportFormSchema } from "../schemas"
import { useFormik } from "formik"
import { createFormikValidate } from "../../utils/formik"
import { useHourReportTimer } from "./use-hour-report-timer"
import { getElapsedHours, TimerState } from "../store"
import { NewHourReportDataFactory } from "../api/new-hour-report-data"
import { endOfDay } from "date-fns"

const getInitialManualFormState = ({ membershipId }: { membershipId: string | null }): HourReportFormState => ({
  date: new Date().toISOString(),
  method: "manual",
  hours: 0,
  minutes: 0,
  membershipId,
})

const getInitialIntervalFormState = ({ membershipId }: { membershipId: string | null }): HourReportFormState => ({
  date: new Date().toISOString(),
  method: "interval",
  startTime: "",
  endTime: "",
  membershipId,
})

const getInitialTimerFormState = ({ membershipId }: { membershipId: string | null }): HourReportFormState => ({
  date: new Date().toISOString(),
  method: "timer",
  timerStartTime: "",
  timerEndTime: "",
  timerElapsedHours: 0,
  membershipId,
})

const getInitialFormState = ({
  method,
  membershipId,
}: {
  method: "manual" | "interval" | "timer"
  membershipId: string | null
}): HourReportFormState => {
  switch (method) {
    case "manual":
      return getInitialManualFormState({ membershipId })
    case "interval":
      return getInitialIntervalFormState({ membershipId })
    case "timer":
      return getInitialTimerFormState({ membershipId })
    default:
      throw new Error(`Unknown method: ${method}`)
  }
}

const createFormState = (
  timerState: TimerState,
  membershipId: string | null,
  initialDate?: string
): HourReportFormState => {
  const defaultFormState = getInitialManualFormState({ membershipId })

  if (timerState.status !== "idle") {
    return mergeTimerWithFormState({ ...defaultFormState, date: initialDate || defaultFormState.date }, timerState)
  }

  return {
    ...defaultFormState,
    date: initialDate || defaultFormState.date,
    membershipId,
  }
}

export const useHourReportForm = ({
  initialDate,
  onSubmitSuccess,
  initialMembershipId = null,
}: UseHourReportFormOptions = {}) => {
  const { createHourReport, meta } = useHourReportMutations()

  const submitIntent = React.useRef<"open" | "submitted">("open")

  const HourReportFormSchema = createHourReportFormSchema()
  const hourReportTimer = useHourReportTimer()

  const formik = useFormik<HourReportFormState>({
    initialValues: createFormState(hourReportTimer.state, initialMembershipId, initialDate),
    validate: createFormikValidate(HourReportFormSchema),
    onSubmit: async (formState, helpers) => {
      if (formState.method === "timer" && hourReportTimer.isIdle) {
        helpers.setFieldError("timerStartTime", "Timer must be started.")
        return
      }

      const formStateWithStatus = { ...formState, status: submitIntent.current }
      const newHourReportData =
        formState.method === "manual"
          ? NewHourReportDataFactory.createFromManualEntry(formStateWithStatus)
          : formState.method === "interval"
          ? NewHourReportDataFactory.createFromIntervalEntry(formStateWithStatus)
          : NewHourReportDataFactory.createFromTimerEntry(
              mergeTimerWithFormState(formStateWithStatus, hourReportTimer.state)
            )

      const createdHourReportId = await createHourReport(newHourReportData)

      if (formState.method === "timer") {
        hourReportTimer.reset()
      }

      helpers.resetForm({
        values: {
          ...getInitialFormState({ method: formState.method, membershipId: formik.values.membershipId }),
          date: formState.date,
        },
      })

      if (createdHourReportId) onSubmitSuccess?.()
    },
  })

  const saveAsDraft = () => {
    submitIntent.current = "open"
    formik.handleSubmit()
  }

  const submitReport = () => {
    submitIntent.current = "submitted"
    formik.handleSubmit()
  }

  return {
    formik,
    saveAsDraft,
    submitReport,
    isSubmitting: formik.isSubmitting || meta.createHourReportState.isLoading,
  }
}

type UseHourReportFormOptions = {
  initialDate?: string
  onSubmitSuccess?: () => void
  initialMembershipId?: string | null
}

const mergeTimerWithFormState = (formState: HourReportFormState, timerState: TimerState): HourReportFormState => {
  if (timerState.status === "idle") return formState
  const timerStateClone = { ...timerState }

  const timerStartTime = new Date(timerStateClone.startTime).toISOString()
  const timerEndTime = guessTimerEndTime(timerStateClone).toISOString()
  const timerElapsedHours = getElapsedHours(timerStateClone)
  const method = "timer" as const

  return {
    ...formState,
    method,
    date: timerStartTime,
    timerStartTime,
    timerEndTime,
    timerElapsedHours,
  } as HourReportFormState
}

const guessTimerEndTime = ({ status, startTime, endTime, currentPauseStart }: TimerState): Date => {
  if (status === "idle") {
    throw new Error("Cannot guess end time for an idle timer")
  }

  const timerStartEndOfDay = endOfDay(new Date(startTime)).getTime()

  if (status === "stopped") {
    return new Date(Math.min(endTime, timerStartEndOfDay))
  }

  if (status === "paused") {
    const tzCurrentPauseStart = new Date(currentPauseStart).getTime()
    return new Date(Math.min(timerStartEndOfDay, tzCurrentPauseStart))
  }

  return new Date(Math.min(Date.now(), timerStartEndOfDay))
}
